import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { CrudTable } from '@/components/CrudTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  getLeaves,
  getLeaveTypes,
  deleteLeave,
  type ILeaveRecord,
  type ILeaveType,
} from '@/api/leave.api';
import { getEmployees, type IEmployee } from '@/api/employee.api';
import { safeArray } from '@/lib/safeArray';
import { getApiErrorMessage } from '@/lib/apiError';
import { Plus, Paperclip, X } from 'lucide-react';

function computeLeaveDays(record: ILeaveRecord): string {
  if (record.halfDay) return '0.5';
  const from = new Date(record.fromDate);
  const to = new Date(record.toDate);
  const diff = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return diff.toString();
}

function formatStaffLabel(emp: IEmployee): string {
  const name = [emp.surname, emp.firstName, emp.middleName].filter(Boolean).join(' ');
  return `${name} (${emp.employeeId})`;
}

const columns: ColumnDef<ILeaveRecord>[] = [
  {
    id: 'staff',
    header: 'Staff',
    size: 22,
    accessorFn: (r) => r.staff.fullName,
    cell: ({ row }) => (
      <div>
        <span className="font-medium">{row.original.staff.fullName}</span>
        <span className="text-xs text-muted-foreground ml-2">{row.original.staff.employeeCode}</span>
        {row.original.staff.isDeleted && (
          <span className="text-xs text-red-500 ml-1">(deleted)</span>
        )}
      </div>
    ),
  },
  {
    id: 'leaveType',
    header: 'Leave Type',
    size: 18,
    accessorFn: (r) => r.leaveType.name,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.leaveType.colorHex && (
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: row.original.leaveType.colorHex }}
          />
        )}
        <span>{row.original.leaveType.name}</span>
      </div>
    ),
  },
  {
    accessorKey: 'fromDate',
    header: 'From',
    size: 11,
    cell: ({ row }) => new Date(row.original.fromDate).toLocaleDateString('en-GB'),
  },
  {
    accessorKey: 'toDate',
    header: 'To',
    size: 11,
    cell: ({ row }) => new Date(row.original.toDate).toLocaleDateString('en-GB'),
  },
  {
    accessorKey: 'days',
    header: 'Days',
    size: 9,
    cell: ({ row }) => {
      const days = computeLeaveDays(row.original);
      const suffix = row.original.halfDay ? ` (${row.original.halfDay})` : '';
      return `${days}${suffix}`;
    },
  },
  {
    accessorKey: 'returningDate',
    header: 'Returning',
    size: 12,
    cell: ({ row }) => new Date(row.original.returningDate).toLocaleDateString('en-GB'),
  },
  {
    accessorKey: 'attachmentUrl',
    header: '',
    size: 7,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.attachmentUrl ? (
        <a
          href={row.original.attachmentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          <Paperclip className="w-4 h-4" />
        </a>
      ) : null,
  },
];

export function LeavesListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(1);

  // Filter state
  const [staffSearch, setStaffSearch] = useState('');
  const [debouncedStaffSearch, setDebouncedStaffSearch] = useState('');
  const [selectedStaff, setSelectedStaff] = useState<IEmployee | null>(null);
  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const staffBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce staff search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedStaffSearch(staffSearch), 300);
    return () => clearTimeout(t);
  }, [staffSearch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedStaff?.id, leaveTypeId, fromDate, toDate]);

  const { data: staffSearchData, isFetching: staffFetching } = useQuery({
    queryKey: ['employees-for-leave-filter', debouncedStaffSearch],
    queryFn: () => getEmployees({ search: debouncedStaffSearch || undefined, limit: 20 }),
    enabled: staffDropdownOpen,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
  const staffResults = staffSearchData?.data ?? [];

  const { data: leaveTypes } = useQuery({
    queryKey: ['leave-types'],
    queryFn: getLeaveTypes,
  });

  const { data: leavesData, isLoading } = useQuery({
    queryKey: ['leaves', page, selectedStaff?.id, leaveTypeId, fromDate, toDate],
    queryFn: () =>
      getLeaves({
        page: page.toString(),
        pageSize: '20',
        sortBy: 'fromDate',
        sortOrder: 'desc',
        ...(selectedStaff?.id ? { staffIds: selectedStaff.id } : {}),
        ...(leaveTypeId ? { leaveTypeIds: leaveTypeId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteLeave,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaves'] });
      toast({ title: 'Leave record cancelled' });
    },
  });

  function handleEdit(row: ILeaveRecord) {
    navigate(`/leaves/${row.id}`);
  }

  async function handleDelete(row: ILeaveRecord) {
    try {
      await deleteMut.mutateAsync(row.id);
    } catch (err: unknown) {
      toast({
        title: getApiErrorMessage(err, 'Failed to cancel leave record'),
        variant: 'destructive',
      });
    }
  }

  function clearFilters() {
    setSelectedStaff(null);
    setStaffSearch('');
    setDebouncedStaffSearch('');
    setLeaveTypeId('');
    setFromDate('');
    setToDate('');
  }

  const hasActiveFilters = !!(selectedStaff || leaveTypeId || fromDate || toDate);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="page-title-leaves">
            Leave Records
          </h1>
          <p className="text-sm text-muted-foreground">Manage employee leave records</p>
        </div>
        <Button onClick={() => navigate('/leaves/new')} data-testid="btn-add-leave">
          <Plus className="w-4 h-4 mr-2" />
          Add Leave
        </Button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-lg border bg-card">
        {/* Staff filter */}
        <div className="flex-1 min-w-[200px] space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Staff</p>
          <div className="relative">
            <Input
              data-testid="filter-staff"
              placeholder="Search by name or code..."
              value={
                staffDropdownOpen
                  ? staffSearch
                  : selectedStaff
                  ? formatStaffLabel(selectedStaff)
                  : staffSearch
              }
              onChange={(e) => {
                if (!staffDropdownOpen) setStaffDropdownOpen(true);
                setStaffSearch(e.target.value);
                if (e.target.value === '') setSelectedStaff(null);
              }}
              onFocus={() => {
                if (staffBlurTimerRef.current) {
                  clearTimeout(staffBlurTimerRef.current);
                  staffBlurTimerRef.current = null;
                }
                setStaffSearch('');
                setStaffDropdownOpen(true);
              }}
              onBlur={() => {
                staffBlurTimerRef.current = setTimeout(() => {
                  setStaffDropdownOpen(false);
                  staffBlurTimerRef.current = null;
                }, 150);
              }}
            />
            {staffDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                {staffFetching ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">Searching...</p>
                ) : staffResults.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No results found</p>
                ) : (
                  staffResults.map((emp: IEmployee) => (
                    <button
                      key={emp.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSelectedStaff(emp);
                        setStaffDropdownOpen(false);
                        setStaffSearch('');
                      }}
                    >
                      {emp.surname} {emp.firstName}
                      {emp.middleName ? ` ${emp.middleName}` : ''}{' '}
                      <span className="text-muted-foreground text-xs">({emp.employeeId})</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Leave Type filter */}
        <div className="flex-1 min-w-[180px] space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Leave Type</p>
          <Select
            value={leaveTypeId || '_all'}
            onValueChange={(v) => setLeaveTypeId(v === '_all' ? '' : v)}
            data-testid="filter-leave-type"
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All types</SelectItem>
              {(leaveTypes ?? []).map((lt: ILeaveType) => (
                <SelectItem key={lt.id} value={lt.id}>
                  <div className="flex items-center gap-2">
                    {lt.colorHex && (
                      <span
                        className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: lt.colorHex }}
                      />
                    )}
                    <span>{lt.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* From Date */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Leave From</p>
          <Input
            data-testid="filter-from-date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40"
          />
        </div>

        {/* To Date */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Leave To</p>
          <Input
            data-testid="filter-to-date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40"
          />
        </div>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="btn-clear-filters">
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      <CrudTable
        columns={columns}
        data={safeArray(leavesData?.data)}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        showSearch={false}
        pagination={
          leavesData
            ? {
                page: leavesData.page,
                limit: leavesData.pageSize,
                total: leavesData.total,
                totalPages: Math.ceil(leavesData.total / leavesData.pageSize),
              }
            : undefined
        }
        onPageChange={setPage}
      />
    </div>
  );
}
